import { useQuery } from '@tanstack/react-query'
import type { ChartData } from 'chart.js'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { useFetchReleasesQuery } from '@/modules/docs/queries/fetch-reseases.query'
import { orderFormParamsLayoutService } from '@/modules/order/services/order-form-params-layout.service'
import { orderFormParamsService } from '@/modules/order/services/order-form-params.service'
import type { TOrderFormParams } from '@/modules/order/types/order-form-params.type'
import { QUERY_KEYS } from '@/modules/shared/constants/query-keys.contant'
import { useMainStore } from '@/modules/shared/stores/main.store'

import { useHomeData } from '../data/home.data'
import { useHomeStore } from '../stores/home.store'
import { version } from './../../../../package.json'
import { useAuth } from '../../shared/hooks/use-auth'
import { useAuthStore } from '../../shared/stores/auth.store'

function parseBooleanParam(value: unknown) {
  const normalizedValue = String(value).trim().toLowerCase()
  if (['true', '1'].includes(normalizedValue)) return true
  if (['false', '0'].includes(normalizedValue)) return false
  return undefined
}

function getMetaPorQuantidade(params?: TOrderFormParams) {
  return parseBooleanParam(
    params?.listaParametroSistema?.find(
      (item) => item.campo === 'MetaPorQuantidade',
    )?.valor,
  )
}

export function useHome() {
  const {
    goalsReport,
    isLoadingGoalsReport,
    isLoadingCustomersReports,
    clientsReports,
    typeDashboard,
  } = useHomeData()


  const { setTypeDashboard } = useHomeStore()

  async function handleChangeTypeDashboard(type: number) {
    if (type === undefined) {
      return
    }
    setTypeDashboard(type)
  }

  const {
    actions: { setIsLoading, setConfirmation, setCloseConfirmation },
  } = useMainStore()

  const { register } = useForm()

  const currentMonth = new Date()

  const chartDataOptions = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
      datalabels: {
        display: false,
      },
    },
  }

  const { actions: { setMetaPorQuantidade } } = useAuthStore()

  useQuery({
    queryKey: [QUERY_KEYS.ORDER_PARAMS_LAYOUT, 'pedido'],
    queryFn: async () => {
      const { body } = await orderFormParamsLayoutService({
        payload: { paramsType: 'pedido' },
      })
      return JSON.parse(body)
    },
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: 3,
    retryDelay: 1000,
  })

  const { data: orderParams } = useQuery<TOrderFormParams>({
    queryKey: [QUERY_KEYS.ORDER_PARAMS, 0],
    queryFn: async () => {
      const { body } = await orderFormParamsService({
        payload: { companyId: 0 },
      })
      return JSON.parse(body)
    },
    staleTime: 0,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    const metaPorQuantidade = getMetaPorQuantidade(orderParams)
    // Preserve the login value until the API provides an explicit setting.
    if (metaPorQuantidade !== undefined) {
      setMetaPorQuantidade(metaPorQuantidade)
    }
  }, [orderParams, setMetaPorQuantidade])

  const valorVendas = goalsReport?.relVendasMetas?.resumo?.valor || 0
  const valorCota = goalsReport?.relVendasMetas?.resumo?.valorCota || 0

  const vendasPercent = valorCota > 0 ? (valorVendas / valorCota) * 100 : 0
  const restantePercent = valorCota > 0 ? 100 - vendasPercent : 0

  const doughnutData: ChartData<'doughnut', number[], unknown> = {
    labels: ['Vendas', 'Restante para a meta'],
    datasets: [
      {
        data: [vendasPercent, restantePercent < 0 ? 0 : restantePercent],
        backgroundColor: ['rgba(84, 227, 142, 0.8)', 'rgba(0, 0, 0, 0.04)'],
        borderColor: ['rgb(84, 212, 141)', 'rgba(0, 0, 0, 0.07)'],
      },
    ],
  }


  const positivacaoDeClientesInitialData = [
    {
      label: 'Novos',
      value: goalsReport?.relResumoPositivacao?.novos || 0,
    },
    {
      label: 'Ativos',
      value: goalsReport?.relResumoPositivacao?.ativos || 0,
    },
    {
      label: 'Inativos há 1 mês',
      value: goalsReport?.relResumoPositivacao?.inativos30 || 0,
    },
    {
      label: 'Inativos há 3 meses',
      value: goalsReport?.relResumoPositivacao?.inativos90 || 0,
    },
    {
      label: 'Inativos há 6 meses',
      value: goalsReport?.relResumoPositivacao?.inativos180 || 0,
    },
    {
      label: 'Inativos há muito tempo',
      value: goalsReport?.relResumoPositivacao?.inativosMais180 || 0,
    },
  ]

  const filteredData = positivacaoDeClientesInitialData.filter(
    (data) => data.value > 0,
  )

  const labels = filteredData.map((data) => data.label)
  const values = filteredData.map((data) => data.value)

  const positivacaoDeClientesData: ChartData<'doughnut', number[], unknown> = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: [
          'rgba(149, 117, 205, 0.8)',
          'rgba(84, 227, 142, 0.8)',
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 205, 86, 0.8)',
          'rgba(255, 159, 64, 0.8)',
          'rgba(229, 115, 115, 0.8)',
        ].slice(0, values.length),
        borderColor: [
          'rgb(149, 117, 205)',
          'rgb(84, 227, 142)',
          'rgb(54, 162, 235)',
          'rgb(255, 205, 86)',
          'rgb(255, 159, 64)',
          'rgb(229, 115, 115)',
        ].slice(0, values.length),
      },
    ],
  }

	const {metaPorQuantidade} = useAuthStore()

  const chartDataGoal = {
    labels: goalsReport?.relVendasMetas?.metasDia?.map((item) => item.dia),
    datasets: [
      {
        label: metaPorQuantidade ? 'Quantidade' : 'Valor',
        data: goalsReport?.relVendasMetas?.metasDia?.map((item) => item.valor),
        borderColor: 'rgb(84, 227, 142)',
        backgroundColor: 'rgba(84, 227, 142, 0.8)',
      },
      {
        label: 'Objetivo',
        data: goalsReport?.relVendasMetas?.metasDia?.map(
          (item) => item.valorCota,
        ),
        borderColor: 'rgb(53, 162, 235)',
        backgroundColor: 'rgba(53, 162, 235, 0.8)',
      },
    ],
  }

  useEffect(() => {
    if (isLoadingGoalsReport) {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  }, [isLoadingGoalsReport])

  return {
    typeDashboard,
    appVersion: version,
    isLoadingGoalsReport,
    isLoadingCustomersReports,
    currentMonth,
    dataGoalReport: goalsReport,
    dataClientReport: clientsReports,
    dataChartGoalReport: {
      data: chartDataGoal,
      options: chartDataOptions,
    },
    inputRef: register,
    handleChangeTypeDashboard,
    doughnutData,
    positivacaoDeClientesData,
    showPercentGoals: vendasPercent && restantePercent !== 0,
    goalsReport,
    showPositivatedClients:
      goalsReport?.relResumoPositivacao?.novos ||
      goalsReport?.relResumoPositivacao?.ativos ||
      goalsReport?.relResumoPositivacao?.inativos30 ||
      goalsReport?.relResumoPositivacao?.inativos90 ||
      goalsReport?.relResumoPositivacao?.inativos180 ||
      goalsReport?.relResumoPositivacao?.inativosMais180,
  }
}
